import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { type PreloadedQuery, graphql, usePreloadedQuery } from 'react-relay';
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import Button from '@atlaskit/button';
import Page, { Grid, GridColumn } from '@atlaskit/page';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import PageHeader from '@atlassian/jira-common-components-page-header/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useHelpPanelLink } from '@atlassian/jira-help-panel-button/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { PersonalSettingsBreadcrumbItem } from '@atlassian/jira-personal-settings-common/src/view/index.tsx';
import { FieldWrapper } from '@atlassian/jira-personal-settings-common/src/view/styled.tsx';
import { GlobalNotificationConfigTableNew } from '@atlassian/jira-personal-settings-notifications-config-table/src/ui/global-notifications-config-table/index.tsx';
import { ProjectNotificationsConfig } from '@atlassian/jira-personal-settings-notifications-config-table/src/ui/project-notifications-config/index.tsx';
import { NOTIFICATIONS_PERSONAL_SETTINGS_PATH } from '@atlassian/jira-personal-settings-paths/src/index.tsx';
import { NotificationPreferencesOptions } from '@atlassian/jira-personal-settings-preference-fields/src/ui/notification-preferences-options-wrapper/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ui_NotificationsPageAndProjectQuery } from '@atlassian/jira-relay/src/__generated__/ui_NotificationsPageAndProjectQuery.graphql';
import { PersonalSettingsNotificationsGlobalTableSkeleton } from '@atlassian/jira-skeletons/src/ui/personal-settings-notifications/PersonalSettingsNotificationsAppSkeleton.tsx';
import messages from './messages.tsx';

const LEARN_MORE_ABOUT_MANAGING_NOTIFICATIONS_HELP_ID = '3Tr3hYdpC7xV6AUcjsfEXW';

const NotificationsPageContent = ({
	queryReference,
}: {
	queryReference: PreloadedQuery<ui_NotificationsPageAndProjectQuery>;
}) => {
	const { formatMessage } = useIntl();
	const data = usePreloadedQuery<ui_NotificationsPageAndProjectQuery>(
		graphql`
			query ui_NotificationsPageAndProjectQuery($cloudId: ID!) {
				...projectNotificationsConfig_personalSettingsNotificationsConfigTable_projects

				jira @required(action: THROW) {
					notificationGlobalPreference(cloudId: $cloudId)
						@required(action: THROW)
						@optIn(to: "JiraNotificationPreference") {
						...globalNotificationsConfigTable_personalSettingsNotificationsConfigTable_GlobalNotificationConfigTableNew
						options @required(action: THROW) {
							...notificationPreferencesOptionsWrapper_personalSettingsPreferenceFields_NotificationPreferencesOptions
						}
					}
				}
			}
		`,
		queryReference,
	);
	return (
		<>
			<FieldWrapper>
				<Wrapper>
					<PreferenceHeading>
						{formatMessage(messages.notificationEmailPreferencesHeading)}
					</PreferenceHeading>
					<Box xcss={preferenceDescStyles}>
						{formatMessage(messages.notificationEmailPreferencesDescriptionWithBatching)}
					</Box>
					<NotificationPreferencesOptions
						emailEnabledFragment={data.jira.notificationGlobalPreference.options}
					/>
				</Wrapper>

				<Placeholder
					name="global-personal-notification-settings"
					fallback={<PersonalSettingsNotificationsGlobalTableSkeleton />}
				>
					<GlobalNotificationConfigTableNew
						allPreferencesData={data.jira.notificationGlobalPreference}
					/>
				</Placeholder>
			</FieldWrapper>
			<ProjectNotificationWrapper>
				<ProjectNotificationsConfig projects={data} />
			</ProjectNotificationWrapper>
		</>
	);
};

export const NotificationsPage = ({
	queryReference,
}: {
	queryReference: PreloadedQuery<ui_NotificationsPageAndProjectQuery>;
}) => {
	const { formatMessage } = useIntl();

	const onOpenInProductHelpArticle = useHelpPanelLink();

	const onLearnMore = useCallback(() => {
		onOpenInProductHelpArticle(LEARN_MORE_ABOUT_MANAGING_NOTIFICATIONS_HELP_ID);
	}, [onOpenInProductHelpArticle]);

	return (
		<JSErrorBoundary
			packageName="jiraPersonalSettingsNotifications"
			id="personalSettingsNotificationsTable"
		>
			<Page testId="personal-settings-notifications.ui.main">
				<Grid layout="fixed" spacing="comfortable">
					<GridColumn medium={12}>
						<PageHeader
							breadcrumbs={
								<Breadcrumbs>
									<PersonalSettingsBreadcrumbItem />
									<BreadcrumbsItem
										href={NOTIFICATIONS_PERSONAL_SETTINGS_PATH}
										text={formatMessage(
											fg('jira-issue-terminology-refresh-m3')
												? messages.notificationsPageTitleIssueTermRefresh
												: messages.notificationsPageTitle,
										)}
									/>
								</Breadcrumbs>
							}
							bottomBar={
								<>
									{formatMessage(messages.notificationsExplanation)}
									<Button spacing="none" appearance="link" onClick={onLearnMore}>
										{formatMessage(messages.notificationsSettingsHelper)}
									</Button>
								</>
							}
						>
							{formatMessage(
								fg('jira-issue-terminology-refresh-m3')
									? messages.notificationsPageTitleIssueTermRefresh
									: messages.notificationsPageTitle,
							)}
						</PageHeader>
						<NotificationsPageContent queryReference={queryReference} />
					</GridColumn>
				</Grid>
				<FireScreenAnalytics />
			</Page>
		</JSErrorBoundary>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PreferenceHeading = styled.h5({
	paddingTop: token('space.075'),
	paddingRight: 0,
	paddingBottom: token('space.100'),
	paddingLeft: 0,
	gap: token('space.200'),
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	marginBottom: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectNotificationWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	paddingTop: token('space.200'),
	paddingRight: 0,
	paddingBottom: token('space.200'),
	paddingLeft: 0,
});

const preferenceDescStyles = xcss({
	marginBottom: 'space.200',
	marginTop: 'space.100',
});
